import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pin, Lock, FileText } from 'lucide-react';
import { notesAPI } from '../../services/api';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';
import NoteEditor from '../../components/NoteEditor';

interface Note {
	note_id: number;
	title: string;
	content: any; // Lexical JSON
	is_pinned: boolean;
	is_archived: boolean;
	is_password_protected: boolean;
	status: string;
	last_modified_at: string;
	created_at: string;
	tag_ids?: number[];
	tag_names?: string[];
	tag_colors?: string[];
	folder_id?: number | null;
}

const Notes: React.FC = () => {
	const { isDark } = useDarkMode();
	const [searchParams, setSearchParams] = useSearchParams();
	const [notes, setNotes] = useState<Note[]>([]);
	const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
	const [selectedNote, setSelectedNote] = useState<Note | null>(null);
	const [loading, setLoading] = useState(true);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [passwordInput, setPasswordInput] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const hasLoggedPageView = useRef(false);

	const folderId = searchParams.get('folder_id') ? parseInt(searchParams.get('folder_id')!) : null;
	const status = searchParams.get('status') || 'active';
	const isArchived = searchParams.get('is_archived') === 'true';
	const tagIds = useMemo(() => {
		const tagIdsParam = searchParams.get('tag_ids');
		return tagIdsParam ? tagIdsParam.split(',').map(id => parseInt(id)) : [];
	}, [searchParams]);

	const loadNotes = useCallback(async (silent: boolean = false) => {
		try {
			if (!silent) setLoading(true);
			const params: any = {
				status,
				is_archived: isArchived
			};
			if (folderId) params.folder_id = folderId;
			if (tagIds.length > 0) params.tag_ids = tagIds.join(',');

			const response = await notesAPI.notes.getAll(params);
			setNotes(response.data.data || []);
		} catch (error) {
			console.error('Error loading notes:', error);
		} finally {
			if (!silent) setLoading(false);
		}
	}, [folderId, status, isArchived, tagIds]);

	useEffect(() => {
		loadNotes(false);
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/notes', 'Notes', 'Notes');
			hasLoggedPageView.current = true;
		}
	}, [loadNotes]);

	useEffect(() => {
		// URL'den note ID al
		const noteIdParam = searchParams.get('note_id');
		if (noteIdParam) {
			const noteId = parseInt(noteIdParam);
			setSelectedNoteId(noteId);
			loadNote(noteId);
		} else {
			setSelectedNoteId(null);
			setSelectedNote(null);
		}
	}, [searchParams]);


	const loadNote = async (noteId: number) => {
		try {
			const response = await notesAPI.notes.getById(noteId);
			const note = response.data.data;
			
			if (note.is_password_protected && !note.content) {
				// Password protected note - show password modal
				setShowPasswordModal(true);
				setSelectedNote(note);
			} else {
				setSelectedNote(note);
			}
		} catch (error) {
			console.error('Error loading note:', error);
		}
	};

	const handleCreateNote = async () => {
		try {
			// Create empty note with default Lexical structure
			const defaultContent = {
				root: {
					children: [
						{
							children: [],
							direction: 'ltr',
							format: '',
							indent: 0,
							type: 'paragraph',
							version: 1
						}
					],
					direction: 'ltr',
					format: '',
					indent: 0,
					type: 'root',
					version: 1
				}
			};

			const response = await notesAPI.notes.create({
				title: 'Yeni Not',
				content: defaultContent,
				folder_id: folderId,
				is_pinned: false,
				is_archived: false,
				is_password_protected: false
			});

			const newNoteId = response.data.data.note_id;
			setSearchParams(prev => {
				const newParams = new URLSearchParams(prev);
				newParams.set('note_id', newNoteId.toString());
				return newParams;
			});
			loadNotes(false);
		} catch (error) {
			console.error('Error creating note:', error);
		}
	};

	const handleNoteClick = (note: Note) => {
		setSearchParams(prev => {
			const newParams = new URLSearchParams(prev);
			newParams.set('note_id', note.note_id.toString());
			return newParams;
		});
	};

	const handleVerifyPassword = async () => {
		if (!selectedNote || !passwordInput) return;

		try {
			const response = await notesAPI.notes.verifyPassword(selectedNote.note_id, passwordInput);
			setSelectedNote(response.data.data);
			setShowPasswordModal(false);
			setPasswordInput('');
			setPasswordError('');
		} catch (error: any) {
			setPasswordError(error.response?.data?.message || 'Invalid password');
		}
	};

	// Extract title from Lexical content (first text node - first line only)
	const getNoteTitle = useCallback((content: any): string => {
		if (!content || !content.root) return 'Yeni Not';
		
		const extractText = (node: any): string => {
			if (node.type === 'text') {
				return node.text || '';
			}
			if (node.children && Array.isArray(node.children)) {
				// Only get text from first child (first paragraph/line)
				if (node.children.length > 0) {
					const firstChild = node.children[0];
					if (firstChild.type === 'text') {
						return firstChild.text || '';
					}
					if (firstChild.children && Array.isArray(firstChild.children)) {
						return firstChild.children.map(extractText).join('');
					}
				}
			}
			return '';
		};

		const text = extractText(content.root);
		// Get first line only (before first newline)
		const firstLine = text.split('\n')[0].trim();
		// Limit to 50 characters for display
		return firstLine ? (firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine) : 'Yeni Not';
	}, []);

	// Debounced save function
	const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const saveNote = useCallback(async (noteId: number, content: any) => {
		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current);
		}

		saveTimeoutRef.current = setTimeout(async () => {
			try {
				const title = getNoteTitle(content);
				
				// Optimizasyon: Lokal olarak seçili notu güncelle
				setSelectedNote(prev => prev && prev.note_id === noteId ? { ...prev, content, title } : prev);
				
				await notesAPI.notes.update(noteId, {
					title,
					content
				});
				loadNotes(true); // Refresh list to update titles without spinner
			} catch (error) {
				console.error('Error saving note:', error);
			}
		}, 1000); // 1 second debounce
	}, [getNoteTitle, loadNotes]);

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin border-emerald-500" />
			</div>
		);
	}

	// Sort notes: pinned first, then by last_modified_at
	const sortedNotes = [...notes].sort((a, b) => {
		if (a.is_pinned && !b.is_pinned) return -1;
		if (!a.is_pinned && b.is_pinned) return 1;
		return new Date(b.last_modified_at).getTime() - new Date(a.last_modified_at).getTime();
	});

	return (
		<div className="flex h-full">
			{/* Notes List - 20% when note selected, 100% when no note selected */}
			<div
				className={`${selectedNoteId ? 'w-1/5' : 'w-full'} border-r ${isDark ? 'border-gray-800' : 'border-gray-200'} flex flex-col`}
			>
				{/* Header */}
				<div className={`p-4 border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
					<button
						onClick={handleCreateNote}
						className={`w-full flex items-center justify-center space-x-2 px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all rounded-none border ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500 hover:text-emerald-300' : 'border-emerald-600/50 bg-white text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600 hover:text-emerald-800'}`}
					>
						<Plus className="w-4 h-4" />
						<span>NEW_NOTE</span>
					</button>
				</div>

				{/* Notes List */}
				<div className="flex-1 overflow-y-auto">
					{sortedNotes.length === 0 ? (
						<div className={`p-8 flex flex-col items-center justify-center text-center font-mono text-xs uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
							<FileText className="w-8 h-8 mb-4 opacity-50" />
							<p>NO_DATA_FOUND</p>
							<p className="text-[9px] mt-2 opacity-70">Initialize a new note sequence to begin</p>
						</div>
					) : (
						<div className="p-2 space-y-1">
							{sortedNotes.map(note => {
								const title = getNoteTitle(note.content);
								const isSelected = selectedNoteId === note.note_id;
								return (
									<div
										key={note.note_id}
										onClick={() => handleNoteClick(note)}
										className={`p-3 cursor-pointer transition-all border-l-2 ${
											isSelected
												? isDark ? 'border-emerald-500 bg-[#111] text-emerald-400' : 'border-emerald-600 bg-emerald-50 text-emerald-700'
												: isDark ? 'border-transparent hover:bg-[#1a1a1a] text-neutral-400' : 'border-transparent hover:bg-neutral-100 text-neutral-600'
										}`}
									>
										<div className="flex items-start justify-between mb-1">
											<h3 className={`font-mono font-bold text-[11px] truncate uppercase tracking-wider flex-1 ${isSelected ? (isDark ? 'text-emerald-300' : 'text-emerald-800') : ''}`}>
												{title}
											</h3>
											<div className="flex items-center space-x-1 ml-2">
												{note.is_pinned && (
													<Pin className={`w-3.5 h-3.5 ${isSelected ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-yellow-500/70' : 'text-yellow-600/70')}`} fill="currentColor" />
												)}
												{note.is_password_protected && (
													<Lock className={`w-3.5 h-3.5 ${isSelected ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-neutral-500' : 'text-neutral-400')}`} />
												)}
											</div>
										</div>
										{note.tag_names && note.tag_names.length > 0 && (
											<div className="flex flex-wrap gap-1 mt-2">
												{note.tag_names.map((tagName, index) => (
													<span
														key={index}
														className="text-[9px] font-mono px-1.5 py-0.5 uppercase tracking-widest border"
														style={{
															borderColor: note.tag_colors?.[index] || '#10b981',
															color: note.tag_colors?.[index] || '#10b981',
															backgroundColor: isDark ? `${note.tag_colors?.[index] || '#10b981'}15` : `${note.tag_colors?.[index] || '#10b981'}10`,
															opacity: isSelected ? 1 : 0.8
														}}
													>
														{tagName}
													</span>
												))}
											</div>
										)}
										<p className={`text-[9px] font-mono mt-2 uppercase tracking-widest ${isSelected ? (isDark ? 'text-emerald-500/70' : 'text-emerald-700/70') : (isDark ? 'text-neutral-600' : 'text-neutral-400')}`}>
											{new Date(note.last_modified_at).toLocaleDateString('en-US', {
												day: '2-digit',
												month: 'short',
												year: 'numeric',
												hour: '2-digit',
												minute: '2-digit'
											})}
										</p>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>

			{/* Note Editor - 80% when note selected, hidden when no note selected */}
			{selectedNoteId && selectedNote && (
				<div key={selectedNote.note_id} className="flex-1 flex flex-col">
					<NoteEditor
						note={selectedNote}
						onSave={(content) => saveNote(selectedNote.note_id, content)}
						onClose={() => {
							setSearchParams(prev => {
								const newParams = new URLSearchParams(prev);
								newParams.delete('note_id');
								return newParams;
							});
						}}
					/>
				</div>
			)}

			{/* Password Modal */}
			{showPasswordModal && selectedNote && createPortal(
				<div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowPasswordModal(false)}>
					<div
						className={`rounded-none border-t-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 w-96 relative overflow-hidden ${isDark ? 'bg-[#0a0a0a] border-emerald-500 border-x border-b border-[#222]' : 'bg-white border-emerald-600 border-x border-b border-neutral-200'}`}
						onClick={(e) => e.stopPropagation()}
					>
						{/* Scanning line animation */}
						<div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(16, 185, 129, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.2) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
						
						<h3 className={`text-sm font-mono font-bold uppercase tracking-widest mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
							[SEC.AUTH_REQ]
						</h3>
						<p className={`text-[10px] font-mono uppercase tracking-widest mb-6 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
							DATA_ENCRYPTED. AWAITING_KEY_PHRASE.
						</p>
						<div className="mb-6 group">
							<div className="relative">
								<div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${isDark ? 'bg-neutral-800 xl:group-focus-within:bg-emerald-500' : 'bg-neutral-200 xl:group-focus-within:bg-emerald-600'}`} />
								<Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${isDark ? 'text-neutral-600 group-focus-within:text-emerald-500' : 'text-neutral-400 group-focus-within:text-emerald-600'}`} />
								<input
									type="password"
									value={passwordInput}
									onChange={(e) => {
										setPasswordInput(e.target.value);
										setPasswordError('');
									}}
									onKeyPress={(e) => e.key === 'Enter' && handleVerifyPassword()}
									className={`w-full pl-10 pr-4 py-2.5 text-xs font-mono uppercase tracking-wider rounded-none focus:outline-none transition-all ${
										isDark 
											? 'bg-[#111] border border-[#333] text-neutral-200 focus:border-emerald-500/50 placeholder-neutral-700' 
											: 'bg-neutral-50 border border-neutral-200 text-neutral-800 focus:border-emerald-600/50 placeholder-neutral-400'
									} ${passwordError ? '!border-red-500/50' : ''}`}
									placeholder="INPUT_KEY..."
									autoFocus
								/>
							</div>
							{passwordError && (
								<p className="text-red-500 text-[9px] font-mono uppercase tracking-widest mt-2">ERR: {passwordError}</p>
							)}
						</div>
						<div className="flex justify-end gap-3">
							<button
								type="button"
								onClick={() => {
									setShowPasswordModal(false);
									setSearchParams(prev => {
										const newParams = new URLSearchParams(prev);
										newParams.delete('note_id');
										return newParams;
									});
								}}
								className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all ${isDark ? 'text-neutral-400 hover:text-white' : 'text-neutral-500 hover:text-black'}`}
							>
								ABORT
							</button>
							<button
								onClick={handleVerifyPassword}
								className={`px-6 py-2 font-mono text-[10px] uppercase tracking-widest border transition-all ${isDark ? 'bg-[#111] border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10' : 'bg-white border-emerald-600/50 text-emerald-700 hover:bg-emerald-50'}`}
							>
								DECRYPT
							</button>
						</div>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Notes;

